import type {
  AysClinicMedicalCareLevel,
  AysClinicSize,
  AysClinicType,
} from '@bindoc-projects/clinic-domain-lib';
import type {
  BdResource,
  BdSharePermission,
} from '@bindoc-projects/resource-domain-lib';
import {
  ShareableType
} from '@bindoc-projects/resource-domain-lib';
import type { AysVirtualClinicAnnualAccountParts } from './annual-account.model';
import type {
  AysVirtualClinicMedical,
  AysVirtualClinicNursing,
} from './staffing.model';
import { BdAdministrativeDistrictId } from '@bindoc-projects/geographic-domain-lib';
import type { Cloneable } from '@bindoc-projects/util-domain-lib';
import { BdDateInterval } from '@bindoc-projects/time-domain-lib';

interface AysVirtualClinicDto {
  id?: string;
  name?: string;
  createdDate?: string;
  modifiedDate?: string;
  administrativeDistrictId?: number;
  address?: AysVirtualClinicAddressDto;
  clinicInformation?: AysVirtualClinicInformationDto;
  highestPermission?: BdSharePermission;
  benchmarkable?: boolean;
}

export class AysVirtualClinic implements BdResource {
  public static fromObj(obj: unknown): AysVirtualClinic {
    const source = obj as Partial<AysVirtualClinicDto>;
    let createdDate = null;
    let modifiedDate = null;
    let address: AysVirtualClinicAddress = undefined;

    if (source.createdDate) {
      createdDate = new Date(source.createdDate);
    }

    if (source.modifiedDate) {
      modifiedDate = new Date(source.modifiedDate);
    }

    if (
      source.administrativeDistrictId !== 0 &&
      source.address &&
      source.address.city
    ) {
      address = AysVirtualClinicAddress.fromObject(source.address);
    }

    const clinicInfo = source.clinicInformation
      ? AysVirtualClinicInformation.fromObject(source.clinicInformation)
      : undefined;

    return new AysVirtualClinic(
      source.id,
      source.name,
      createdDate,
      modifiedDate,
      source.highestPermission,
      address,
      clinicInfo,
      source.benchmarkable,
      undefined,
    );
  }

  public static fromObjArr(objArr: unknown[]): AysVirtualClinic[] {
    return objArr.map(AysVirtualClinic.fromObj);
  }

  public resourceName: ShareableType = ShareableType.VIRTUAL_CLINIC;

  constructor(
    public id?: string,
    public name?: string,
    public createdDate?: Date,
    public modifiedDate?: Date,
    public highestPermission?: BdSharePermission,
    public address?: AysVirtualClinicAddress,
    public clinicInformation?: AysVirtualClinicInformation,
    public benchmarkable?: boolean,
    public hasError?: boolean,
    public labels?: string[],
  ) {}

  clone(): AysVirtualClinic {
    return new AysVirtualClinic(
      this.id,
      this.name,
      this.createdDate,
      this.modifiedDate,
      this.highestPermission,
      this.address?.clone(),
      this.clinicInformation?.clone(),
      this.benchmarkable,
      this.hasError,
      this.labels ? [...this.labels] : undefined,
    );
  }
}

interface AysVirtualClinicInformationDto {
  origin?: AysVirtualClinicInformation['origin'];
  costDataExists?: boolean;
  postalCodeExists?: boolean;
  referralDataExists?: boolean;
  dismissalDateRange?: { from: string; to: string };
}

export class AysVirtualClinicInformation implements Cloneable<AysVirtualClinicInformation> {
  public static fromObject(obj: unknown): AysVirtualClinicInformation {
    const source = obj as Partial<AysVirtualClinicInformationDto>;
    let drgCasesInformation: BdDateInterval | undefined = undefined;

    if (!!source && !!source.dismissalDateRange) {
      drgCasesInformation = new BdDateInterval(
        new Date(source.dismissalDateRange.from),
        new Date(source.dismissalDateRange.to),
      );
    }
    return new AysVirtualClinicInformation(
      source.origin,
      source.costDataExists,
      source.postalCodeExists,
      source.referralDataExists,
      drgCasesInformation,
    );
  }

  constructor(
    public origin:
      | {
          type: 'UserCreated';
          clinicIk: string | null;
          clinicSn: string[];
          virtualClinicId: string;
        }
      | {
          type: 'Split';
          originVirtualClinicId: string;
          clinicIk: string | null;
          clinicSn: string[];
          virtualClinicId: string;
        }
      | {
          type: 'Merge';
          originVirtualClinicIds: string[];
          clinicIk: string | null;
          clinicSn: string[];
          virtualClinicId: string;
        }
      | null,
    public costDataExists: boolean,
    public postalCodeExists: boolean,
    public referralDataExists: boolean,
    public dismissalDateRange?: BdDateInterval,
  ) {}

  public clone(): AysVirtualClinicInformation {
    return new AysVirtualClinicInformation(
      JSON.parse(JSON.stringify(this.origin)),
      this.costDataExists,
      this.postalCodeExists,
      this.referralDataExists,
      this.dismissalDateRange ? this.dismissalDateRange.clone() : undefined,
    );
  }

  public toLabels(): string[] {
    const labels: string[] = [];
    if (this.postalCodeExists) {
      labels.push('PLZ-Analyse');
    }
    if (this.costDataExists) {
      labels.push('Kosten-Daten');
    }
    if (this.referralDataExists) {
      labels.push('Einweiser-Daten');
    }
    return labels;
  }
}

interface AysVirtualClinicAddressDto {
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  location?: number[];
  administrativeDistrictId?: string | number;
}

export class AysVirtualClinicAddress implements Cloneable<AysVirtualClinicAddress> {
  public static fromObject(obj: unknown) {
    const source = obj as Partial<AysVirtualClinicAddressDto>;
    return new AysVirtualClinicAddress(
      source.street,
      source.houseNumber,
      source.postalCode,
      source.city,
      source.country,
      source.location,
      source.administrativeDistrictId
        ? BdAdministrativeDistrictId.create(source.administrativeDistrictId)
        : null,
    );
  }

  constructor(
    public street?: string,
    public houseNumber?: string,
    public postalCode?: string,
    public city?: string,
    public country?: string,
    public location?: number[],
    public administrativeDistrictId?: BdAdministrativeDistrictId,
  ) {}

  public isEmpty(): boolean {
    return (
      this.street === undefined &&
      this.houseNumber === undefined &&
      this.city === undefined &&
      this.postalCode === undefined
    );
  }

  /** `Musterstraße 1, 12345 Musterstadt`; a missing part is left out instead of printing `undefined`. */
  public toString(): string {
    const streetLine = [this.street, this.houseNumber]
      .filter(Boolean)
      .join(' ');
    const cityLine = [this.postalCode, this.city].filter(Boolean).join(' ');
    return [streetLine, cityLine].filter(Boolean).join(', ');
  }

  clone(): AysVirtualClinicAddress {
    return new AysVirtualClinicAddress(
      this.street,
      this.houseNumber,
      this.postalCode,
      this.city,
      this.country,
      this.location,
      this.administrativeDistrictId?.clone(),
    );
  }
}

interface AysVirtualClinicAdditionalInfoDto {
  virtualClinicId?: string;
  year?: number;
  beds?: number;
  clinicSize?: AysClinicSize;
  clinicType?: AysClinicType;
  isUniversity?: boolean;
  medicalCareLevel?: AysClinicMedicalCareLevel;
}

export class AysVirtualClinicAdditionalInfo {
  public static fromObjArr(
    objArr: unknown[],
  ): AysVirtualClinicAdditionalInfo[] {
    return objArr.map(AysVirtualClinicAdditionalInfo.fromObj);
  }

  public static fromObj(obj: unknown): AysVirtualClinicAdditionalInfo {
    const source = obj as Partial<AysVirtualClinicAdditionalInfoDto>;
    return new AysVirtualClinicAdditionalInfo(
      source.virtualClinicId,
      source.year,
      source.beds,
      source.clinicSize,
      source.clinicType,
      source.isUniversity,
      source.medicalCareLevel,
    );
  }

  constructor(
    public virtualClinicId: string,
    public year: number,
    public beds: number,
    public clinicSize?: AysClinicSize,
    public clinicType?: AysClinicType,
    public isUniversity?: boolean,
    public medicalCareLevel?: AysClinicMedicalCareLevel,
  ) {}
}

interface AysVirtualClinicAnnualAccountDto {
  year?: number;
  virtualClinicId?: string;
  parts?: AysVirtualClinicAnnualAccountParts;
}

export class AysVirtualClinicAnnualAccount {
  constructor(
    public year: number,
    public virtualClinicId: string,
    public parts?: AysVirtualClinicAnnualAccountParts,
  ) {}

  public static fromObj(obj: unknown): AysVirtualClinicAnnualAccount {
    const source = obj as Partial<AysVirtualClinicAnnualAccountDto>;
    return new AysVirtualClinicAnnualAccount(
      source.year,
      source.virtualClinicId,
      source.parts,
    );
  }

  public static fromObjArr(objArr: unknown[]): AysVirtualClinicAnnualAccount[] {
    return objArr.map(AysVirtualClinicAnnualAccount.fromObj);
  }
}

interface AysVirtualClinicStaffingDto {
  year?: number;
  virtualClinicId?: string;
  specialtyDepartmentId?: string;
  nursing?: AysVirtualClinicNursing;
  medical?: AysVirtualClinicMedical;
}

export class AysVirtualClinicStaffing {
  public static fromObjArr(objArr: unknown[]): AysVirtualClinicStaffing[] {
    return objArr.map(AysVirtualClinicStaffing.fromObj);
  }

  public static fromObj(obj: unknown): AysVirtualClinicStaffing {
    const source = obj as Partial<AysVirtualClinicStaffingDto>;
    return new AysVirtualClinicStaffing(
      source.year,
      source.virtualClinicId,
      source.specialtyDepartmentId,
      source.nursing,
      source.medical,
    );
  }

  constructor(
    public year: number,
    public virtualClinicId: string,
    public specialtyDepartmentId?: string,
    public nursing?: AysVirtualClinicNursing,
    public medical?: AysVirtualClinicMedical,
  ) {}
}

interface AysVirtualClinicCaseNumberDto {
  year?: number;
  virtualClinicId?: string;
  specialtyDepartmentId?: string;
  cmi?: number;
  caseCount?: number;
}

export class AysVirtualClinicCaseNumber {
  public static fromObjArr(objArr: unknown[]): AysVirtualClinicCaseNumber[] {
    return objArr.map(AysVirtualClinicCaseNumber.fromObj);
  }

  public static fromObj(obj: unknown): AysVirtualClinicCaseNumber {
    const source = obj as Partial<AysVirtualClinicCaseNumberDto>;
    return new AysVirtualClinicCaseNumber(
      source.year,
      source.virtualClinicId,
      source.specialtyDepartmentId,
      source.cmi,
      source.caseCount,
    );
  }

  constructor(
    public year: number,
    public virtualClinicId: string,
    public specialtyDepartmentId?: string,
    public cmi?: number,
    public caseCount?: number,
  ) {}
}
